/**
 * "This page is already being replaced" — said by whatever started the
 * full-page load, read by anything about to report a failure or start a load
 * of its own.
 *
 * 🚨 IMPORT-FREE, and it must stay so. The root and global error boundaries
 * import it, and they are the screens that have to render when a chunk could
 * not be fetched (see `boundary-recovery.ts`).
 *
 * ── WHY ────────────────────────────────────────────────────────────────────
 *
 * When a page starts a full-page load in the iOS app's WebView, every request
 * the old page still has in flight fails at once with "Load failed". Nothing
 * is wrong with the network; the page is going. But the page is still running
 * for a moment, and anything that reports a failed load in that moment files
 * a fault that never happened.
 *
 * All five events behind MUCKA-WEB-CV and MUCKA-WEB-C3 (7–12 Oct 2026, every
 * one the iOS app) were a full-page load of our own starting at the moment of
 * the failure. Four show a document request from the same device reaching the
 * server within about a quarter of a second of it, with no tap that asked for
 * one; the fifth is the stuck-navigation recovery, whose own log line names
 * the very chunk that then "failed". In the three wake-word events every
 * request the page had in flight, API calls included, failed in the same two
 * milliseconds. The loads were an update being applied (`UpdatePrompt`), the
 * stuck-navigation recovery and a push tap's hard navigation; the request log
 * does not say which code started the other two.
 *
 * And a second full-page load started while the first is still in flight
 * does harm of its own. WebKit cancels the first, and Capacitor answers ANY
 * failed navigation, a cancellation included, by loading the bundled "You're
 * offline" page (`errorPath`; see `WebViewDelegationHandler` in
 * `@capacitor/ios`). On 12 Oct the stuck-navigation recovery navigated, the
 * chunk it had been waiting on was cancelled by that navigation, the error
 * boundary reloaded on top of it, and the phone then made ten requests from
 * the offline page's boot recovery in under nine seconds — consistent with
 * that collision, though not proven by it.
 *
 * So whoever starts an automatic full-page load says so here first, and:
 *   · a chunk boundary does not start a second one (`recoverFromChunkFailure`);
 *   · reports name it (`page_leaving`), so the next event says which load it
 *     was instead of leaving it to be pieced together from the request log.
 *
 * Only AUTOMATIC loads mark it. A button the user pressed is a load they
 * asked for, and nothing is reported from under it.
 */

export type PageLeavingReason =
  /** `UpdatePrompt` applying a waiting service worker. */
  | 'update'
  /** `stuck-navigation-watch` getting a wedged page to where they tapped. */
  | 'stuck-navigation'
  /** An error boundary reloading for a chunk that would not download. */
  | 'chunk-reload'
  /** A push notification tap's hard navigation (`native/push.ts`). */
  | 'notification-tap';

/**
 * How long a mark counts for. A full-page load that has not replaced the page
 * by now has failed or been refused, and this page is staying: after it, a
 * boundary may reload again and a failure is a failure.
 */
export const PAGE_LEAVING_WINDOW_MS = 10_000;

let mark: { reason: PageLeavingReason; at: number } | null = null;

/**
 * Say that this page is about to be replaced. Call it immediately BEFORE the
 * `location.reload()` / `assign()` / `href =`, never after: the old page's
 * requests fail as the new load starts.
 *
 * The first mark inside the window wins, because the first load is the one
 * that cancelled everything in flight.
 */
export function notePageLeaving(reason: PageLeavingReason): void {
  const now = Date.now();
  if (mark && now - mark.at < PAGE_LEAVING_WINDOW_MS) return;
  mark = { reason, at: now };
}

/** Is this page being replaced by a load we started? Null if not, or no longer. */
export function pageLeaving(): { reason: PageLeavingReason; msAgo: number } | null {
  if (!mark) return null;
  const msAgo = Date.now() - mark.at;
  if (msAgo >= PAGE_LEAVING_WINDOW_MS) return null;
  return { reason: mark.reason, msAgo };
}

/** Test seam. */
export function resetPageLeavingForTests(): void {
  mark = null;
}
